'use client';

import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { forwardRef, memo, useImperativeHandle, useState } from 'react';

export type TableAGRowSelectedRefs = {
    onUpdateRowSelected: (rowSelected?: any[]) => void;
    onUpdateRowDisplayed: (displayed: number) => void;
};

type Props = {
    onClearRowSelected: VoidFunction;
};

/**
 * Hiển thị thông tin số lượng các rows đang được chọn và nút clear đi các row được chọn đó
 *
 * @description lưu ý là component này chỉ update khi click select trên table. Việc gọi thông qua API của tableAG sẽ ảnh hướng đến việc này
 *  */
const TableAGRowSelected = forwardRef<TableAGRowSelectedRefs, Props>(
    (props, ref) => {
        const transDef = useTranslations('System');
        const [totalRowSelected, setTotalRowSelected] = useState<number>(0);
        const [totalRowDisplayed, setTotalRowDisplayed] = useState<number>(0);

        const onUpdateRowSelected = (rowSelected?: any[]) => {
            setTotalRowSelected(rowSelected?.length ?? 0);
        };

        const onUpdateRowDisplayed = (displayed: number) => {
            setTotalRowDisplayed(displayed);
        };

        // This is the function that will be called when the X icon is clicked
        const onClearSelection = () => {
            props.onClearRowSelected();
        };

        useImperativeHandle(ref, () => ({
            onUpdateRowSelected,
            onUpdateRowDisplayed,
        }));

        if (totalRowSelected === 0 && totalRowDisplayed === 0) {
            return null;
        }

        return (
            <p className='p-1 text-[13px] font-medium max-lg:w-full'>
                {totalRowDisplayed > 0 && (
                    <span>Lọc: {totalRowDisplayed} hàng</span>
                )}

                {totalRowDisplayed > 0 && totalRowSelected > 0 && (
                    <span> | </span>
                )}

                {totalRowSelected > 0 && (
                    <>
                        <span>
                            {transDef('System_Selected')}: {totalRowSelected}{' '}
                            {transDef('System_SelectedRow')}
                        </span>
                        <X
                            role='button'
                            xlinkTitle={transDef('System_UnSelect')}
                            className='ml-1 inline-block cursor-pointer text-red-500 hover:scale-125'
                            size={13}
                            strokeWidth={2}
                            onClick={onClearSelection}
                        />
                    </>
                )}
            </p>
        );
    }
);

TableAGRowSelected.displayName = 'TableAGRowSelected';
export default memo(TableAGRowSelected);
